import React from 'react';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';
import * as Sentry from '@sentry/react';

/**
 * DSN do projeto no Sentry. Hardcoded de propósito — DSN é público por design
 * (fica visível no bundle de qualquer forma) e não é credencial.
 */
const DSN =
  'https://ba3fb6f5bdd852689bf211d60e8c74bd@o4511644066316288.ingest.us.sentry.io/4511859583025152';

/**
 * Por padrão o Sentry só roda em build de produção — senão todo erro do dev local
 * cairia no mesmo projeto. Vire pra `true` temporariamente pra testar a integração
 * rodando `pnpm dev`, e volte pra `false` depois.
 */
const ENABLE_IN_DEV = false;

/**
 * Inicializa o Sentry. Deve ser a primeira coisa executada no boot (ver main.tsx),
 * senão erros durante o próprio boot escapam da captura.
 *
 * Quando não inicializa, todas as APIs do Sentry (setUser, ErrorBoundary, etc.)
 * viram no-op — nenhum call site precisa checar se está ligado.
 */
export function initSentry(): void {
  const isProd = import.meta.env.PROD;
  if (!isProd && !ENABLE_IN_DEV) return;

  Sentry.init({
    dsn: DSN,
    environment: isProd ? 'production' : 'development',

    // `release` não é setado de propósito: o @sentry/vite-plugin injeta o valor no
    // bundle durante o build e ele casa com os source maps enviados. Setar aqui na mão
    // quebraria esse pareamento.

    integrations: [
      Sentry.reactRouterBrowserTracingIntegration({
        useEffect: React.useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
    ],

    // 10% das navegações em prod (ferramenta interna, cota curta), tudo em dev.
    tracesSampleRate: isProd ? 0.1 : 1.0,

    // Replay só quando estoura erro — sessão normal não é gravada.
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
  });

  // A integração do Replay entra depois, num chunk à parte (ver replay.ts):
  // estaticamente ela sozinha dobrava o peso do Sentry no bundle inicial.
  // requestIdleCallback pra não disputar CPU com a primeira renderização.
  const scheduleReplay = () => {
    void import('./replay').then((m) => m.enableReplay()).catch(() => {
      /* replay é best-effort — falhar aqui não pode derrubar o app */
    });
  };
  const idle: typeof window.requestIdleCallback | undefined =
    typeof window.requestIdleCallback === 'function' ? window.requestIdleCallback.bind(window) : undefined;
  if (idle) idle(scheduleReplay, { timeout: 5000 });
  else window.setTimeout(scheduleReplay, 2000);
}
